import { DAY_KEYS, type DayKey } from "@/lib/days";
import {
  MEAL_METRICS,
  type Meal,
  type MealCondition,
  type MealLogConfig,
  type MealLogDayData,
  type MealMetric,
} from "@/lib/schema";

export function asMealLogDayData(dayData: unknown): MealLogDayData {
  return (dayData ?? { meals: [], finished: false }) as MealLogDayData;
}

export function sumMetrics(
  items: ReadonlyArray<Record<MealMetric, number>>,
): Record<MealMetric, number> {
  const totals: Record<MealMetric, number> = {
    kcal: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
  };
  for (const item of items) {
    for (const metric of MEAL_METRICS) totals[metric] += item[metric];
  }
  return totals;
}

export function dayTotals(meals: Meal[]): Record<MealMetric, number> {
  return sumMetrics(meals);
}

export function isConditionMet(
  condition: MealCondition,
  totals: Record<MealMetric, number>,
): boolean {
  const total = totals[condition.metric];
  // Inclusive boundaries: exactly at the threshold counts as met.
  return condition.direction === "atMost"
    ? total <= condition.threshold
    : total >= condition.threshold;
}

export function scoreDay(
  config: MealLogConfig,
  dayData: unknown,
  _dayKey: DayKey,
): number {
  const data = asMealLogDayData(dayData);
  if (!data.finished) return 0;
  if (config.conditions.length === 0) return 0;
  const totals = dayTotals(data.meals);
  let score = 0;
  for (const condition of config.conditions) {
    if (isConditionMet(condition, totals)) score += condition.points;
  }
  return score;
}

export function scoreWeek(
  config: MealLogConfig,
  runtime: { week: unknown; days: Record<DayKey, unknown> },
): number {
  let total = 0;
  for (const k of DAY_KEYS) total += scoreDay(config, runtime.days[k], k);
  return total;
}

export function maxScoreDay(config: MealLogConfig): number {
  return config.conditions.reduce((sum, c) => sum + c.points, 0);
}

export function maxScoreWeek(config: MealLogConfig): number {
  return 7 * maxScoreDay(config);
}
